import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Package, Plus, Upload, Loader2, Trash2, X, ImageIcon, ZoomIn,
  ChevronLeft, ChevronRight, Pencil, Tag
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductStudio() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/products`, { withCredentials: true });
      setProducts(res.data.products || []);
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/api/products/${id}`, { withCredentials: true });
      toast.success("Product deleted");
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="product-studio-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 space-y-10" data-testid="product-studio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6" />
            Product Studio
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your product images for video campaigns</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="rounded-full gap-2" disabled={products.length >= 5} data-testid="create-product-btn">
          <Plus className="w-4 h-4" />{products.length >= 5 ? "Limit Reached (5/5)" : "Add Product"}
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <ProductForm
          categories={categories}
          onSuccess={() => { setShowCreate(false); fetchProducts(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Products Gallery */}
      <section>
        <h2 className="text-lg font-semibold mb-4">
          My Products <span className="text-sm font-normal text-muted-foreground">({products.length}/5)</span>
        </h2>
        {products.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-border rounded-xl">
            <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No products yet.</p>
            <Button variant="outline" size="sm" className="mt-3 rounded-full gap-1" onClick={() => setShowCreate(true)}>
              <Plus className="w-3 h-3" />Add Your First Product
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onDelete={deleteProduct}
                onEdit={setEditingProduct}
                onPreview={(idx) => setLightbox({ product, index: idx })}
              />
            ))}
          </div>
        )}
      </section>

      {/* Edit Modal */}
      {editingProduct && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditingProduct(null)}>
          <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <EditProductForm
              product={editingProduct}
              categories={categories}
              onSuccess={() => { setEditingProduct(null); fetchProducts(); }}
              onCancel={() => setEditingProduct(null)}
            />
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <ProductLightbox
          product={lightbox.product}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}


function ProductCard({ product, onDelete, onEdit, onPreview }) {
  const images = product.images || [];
  const gridCols = images.length >= 3 ? "grid-cols-3" : images.length === 2 ? "grid-cols-2" : "grid-cols-1";

  return (
    <div
      className="group relative rounded-xl border border-border bg-card overflow-hidden transition-shadow hover:shadow-md"
      data-testid={`product-card-${product.id}`}
    >
      {/* Images */}
      <div className={`grid ${gridCols} gap-px bg-border`}>
        {images.slice(0, 3).map((img, i) => (
          <button
            key={i}
            className="aspect-square bg-muted relative cursor-pointer group/img"
            onClick={() => onPreview?.(i)}
          >
            <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
            </div>
          </button>
        ))}
      </div>
      {images.length > 3 && (
        <div className="text-center py-0.5 bg-muted/50">
          <span className="text-[10px] text-muted-foreground">+{images.length - 3} more</span>
        </div>
      )}

      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-semibold truncate">{product.name}</h3>
        {product.category && product.category !== "Other" && (
          <span className="inline-block text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium mt-1">{product.category}</span>
        )}
        {product.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
        )}
        {product.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {product.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="text-[9px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{tag}</span>
            ))}
            {product.tags.length > 3 && <span className="text-[9px] text-muted-foreground">+{product.tags.length - 3}</span>}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(product); }}
            className="w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80"
            data-testid={`edit-product-${product.id}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(product.id); }}
            className="w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-red-600/80"
            data-testid={`delete-product-${product.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}


function ProductForm({ categories, onSuccess, onCancel }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Other");
  const [tagsInput, setTagsInput] = useState("");
  const [slots, setSlots] = useState([
    { file: null, preview: null },
    { file: null, preview: null },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const fileRefs = useRef([]);

  const addSlot = () => {
    if (slots.length < 3) setSlots([...slots, { file: null, preview: null }]);
  };

  const handleFileChange = (index, file) => {
    if (!file) return;
    const newSlots = [...slots];
    newSlots[index] = { ...newSlots[index], file };
    const reader = new FileReader();
    reader.onload = (e) => {
      newSlots[index].preview = e.target.result;
      setSlots([...newSlots]);
    };
    reader.readAsDataURL(file);
    setSlots(newSlots);
  };

  const removeSlot = (index) => {
    if (slots.length <= 2) return;
    setSlots(slots.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    const filledSlots = slots.filter((s) => s.file);
    if (filledSlots.length < 2) return toast.error("At least 2 images are required");

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("description", description.trim());
      formData.append("category", category);
      formData.append("tags", tagsInput.trim());
      slots.forEach((s, i) => {
        if (s.file) formData.append(`image${i + 1}`, s.file);
      });

      await axios.post(`${API}/api/products`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Product created!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create product");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="create-product-form">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Add Product</h3>
          <p className="text-xs text-muted-foreground">Upload 2-3 product images with details</p>
        </div>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vitamin C Serum" data-testid="product-name" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Category</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="product-category"
          >
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Description</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short product description..." data-testid="product-description" />
      </div>

      <div className="space-y-2">
        <Label className="text-xs flex items-center gap-1"><Tag className="w-3 h-3" />Tags (comma-separated)</Label>
        <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="e.g. skincare, vitamin c, serum" data-testid="product-tags" />
      </div>

      {/* Image uploads */}
      <div>
        <Label className="text-xs mb-2 block">Product Images ({slots.length}/3)</Label>
        <div className="flex flex-wrap gap-3">
          {slots.map((slot, i) => (
            <div key={i} className="relative">
              <input
                ref={(el) => (fileRefs.current[i] = el)}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(i, e.target.files[0])}
              />
              <button
                type="button"
                onClick={() => fileRefs.current[i]?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center overflow-hidden bg-muted/30"
              >
                {slot.preview ? (
                  <img src={slot.preview} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <>
                    <Upload className="w-4 h-4 text-muted-foreground" />
                    <span className="text-[9px] text-muted-foreground">{i + 1}</span>
                  </>
                )}
              </button>
              {slots.length > 1 && (
                <button
                  onClick={() => removeSlot(i)}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[8px]"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
          {slots.length < 3 && (
            <button
              onClick={addSlot}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center"
            >
              <Plus className="w-4 h-4 text-muted-foreground" />
              <span className="text-[9px] text-muted-foreground">Add</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="rounded-full">Cancel</Button>
        <Button size="sm" onClick={handleSubmit} disabled={submitting} className="rounded-full gap-1" data-testid="product-submit">
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {submitting ? "Creating..." : "Add Product"}
        </Button>
      </div>
    </div>
  );
}


function EditProductForm({ product, categories, onSuccess, onCancel }) {
  const [name, setName] = useState(product.name || "");
  const [description, setDescription] = useState(product.description || "");
  const [category, setCategory] = useState(product.category || "Other");
  const [tagsInput, setTagsInput] = useState((product.tags || []).join(", "));
  const existingImages = product.images || [];
  const [previews, setPreviews] = useState([...existingImages]);
  const [files, setFiles] = useState(new Array(existingImages.length).fill(null));
  const [submitting, setSubmitting] = useState(false);
  const fileRefs = useRef([]);

  const handleFileChange = (index, file) => {
    if (!file) return;
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);
    const reader = new FileReader();
    reader.onload = (e) => {
      const np = [...previews];
      np[index] = e.target.result;
      setPreviews(np);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("description", description.trim());
      formData.append("category", category);
      formData.append("tags", tagsInput.trim());
      files.forEach((f, i) => {
        if (f) formData.append(`image${i + 1}`, f);
      });
      await axios.put(`${API}/api/products/${product.id}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Product updated!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update product");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="edit-product-form">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Edit Product</h3>
          <p className="text-xs text-muted-foreground">Update details or replace images</p>
        </div>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-product-name" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Category</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="edit-product-category"
          >
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Description</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="edit-product-description" />
      </div>

      <div className="space-y-2">
        <Label className="text-xs flex items-center gap-1"><Tag className="w-3 h-3" />Tags</Label>
        <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} data-testid="edit-product-tags" />
      </div>

      <div>
        <Label className="text-xs mb-2 block">Images (click to replace)</Label>
        <div className="flex flex-wrap gap-3">
          {previews.map((prev, i) => (
            <div key={i} className="relative">
              <input
                ref={(el) => (fileRefs.current[i] = el)}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(i, e.target.files[0])}
              />
              <button
                type="button"
                onClick={() => fileRefs.current[i]?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors overflow-hidden bg-muted/30 relative group/edit"
              >
                <img src={prev} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover/edit:bg-black/30 transition-colors flex items-center justify-center">
                  <Pencil className="w-4 h-4 text-white opacity-0 group-hover/edit:opacity-100 transition-opacity" />
                </div>
              </button>
              {files[i] && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-[8px] text-white font-bold">!</span>
                </span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Only replaced images will be re-uploaded</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="rounded-full">Cancel</Button>
        <Button size="sm" onClick={handleSubmit} disabled={submitting} className="rounded-full gap-1" data-testid="edit-product-submit">
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pencil className="w-3 h-3" />}
          {submitting ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}


function ProductLightbox({ product, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex || 0);
  const images = product.images || [];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((p) => (p + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((p) => (p - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [images.length, onClose]);

  if (images.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={onClose} data-testid="product-lightbox">
      <div className="relative max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" data-testid="product-lightbox-close">
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-4">
          <h3 className="text-white font-semibold text-lg">{product.name}</h3>
          {product.category && product.category !== "Other" && (
            <span className="inline-block text-xs bg-white/10 text-white/70 px-2 py-0.5 rounded-full mt-1">{product.category}</span>
          )}
        </div>

        <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
          <img src={images[index]} alt={`${product.name} ${index + 1}`} className="w-full h-full object-contain" data-testid="product-lightbox-main" />
          {images.length > 1 && (
            <>
              <button
                onClick={() => setIndex((p) => (p - 1 + images.length) % images.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIndex((p) => (p + 1) % images.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                  i === index ? "border-white scale-105" : "border-transparent opacity-60 hover:opacity-90"
                }`}
              >
                <img src={img} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <p className="text-center text-white/50 text-xs mt-2">{index + 1} / {images.length}</p>
      </div>
    </div>
  );
}
